import { useMemo } from 'react'
import { useText, type FooterProps, type NavEntry } from '@kkf/design'
import { BOTTOM_NAV, FOOTER, HEADER_CTA, NAV, SIDEBAR, FOOTER_POSTS } from '../data/shell'
import { NOT_FOUND } from '../data/not-found'
import { useT } from '../i18n'

/**
 * The shell's content, in the reader's language.
 *
 * `data/shell.ts` holds catalogue **keys** for its labels and `Text` for its prose;
 * `@kkf/design`'s `Header`, `Footer` and `MobileSidebar` take plain strings. This is the
 * seam, and there is one per app — see the resort's namesake for why it is not shared.
 *
 * This shell used to carry one thing the others did not — a **Products mega panel**, whose
 * cards were built here because a product's category, name and description were `Text` on
 * `PRODUCTS` rather than catalogue keys. The shop came off on 2026-09-19 and the panel with
 * it; `Partner Products` is an ordinary `href` entry now and needs nothing special.
 */
export function useShellContent() {
  const t = useT()
  const s = useText()

  return useMemo(() => {
    const link = <T extends { labelKey: Parameters<typeof t>[0] }>(entry: T) => {
      const { labelKey, ...rest } = entry
      return { label: t(labelKey), ...rest }
    }

    return {
      nav: NAV.map(link) as NavEntry[],
      headerCta: link(HEADER_CTA),
      footer: {
        preamble: {
          subTitle: FOOTER.preamble.subTitle,
          title: s(FOOTER.preamble.title),
          paragraphs: FOOTER.preamble.paragraphs.map(s),
          image: FOOTER.preamble.image,
          imageAlt: s(FOOTER.preamble.imageAlt),
        },
        wayfinding: {
          label: t(FOOTER.wayfinding.labelKey),
          links: FOOTER.wayfinding.links.map(link),
        },
        logo: FOOTER.logo,
        homeTo: FOOTER.homeTo,
        about: s(FOOTER.about),
        socials: FOOTER.socials,
        quickLinks: {
          title: t(FOOTER.quickLinks.titleKey),
          links: FOOTER.quickLinks.links.map(link),
        },
        /*
         * This site's own posts when it has any, and the two curated invitations when it
         * does not — see `FOOTER_POSTS` in `data/shell`.
         *
         * The two halves have different shapes on purpose and the seam is here rather than
         * in the data module. A post's title and date are written once, in markdown, in one
         * language; the invitations are editorial `Text` written in both. Resolving the
         * curated ones through `s` and passing the posts straight through is what keeps a
         * post's real title from being run through a translation lookup that has no entry
         * for it.
         */
        recentPosts: {
          title: s(FOOTER.recentPosts.title),
          posts:
            FOOTER_POSTS.length > 0
              ? FOOTER_POSTS
              : FOOTER.recentPosts.posts.map((post) => ({
                  image: post.image,
                  date: s(post.date),
                  title: s(post.title),
                  to: post.to,
                })),
        },
        contact: {
          title: t(FOOTER.contact.titleKey),
          links: FOOTER.contact.links.map((entry) =>
            entry.labelKey
              ? { icon: entry.icon, label: t(entry.labelKey) }
              : { icon: entry.icon, label: entry.label ?? '', href: entry.href },
          ),
          newsletterPlaceholder: t(FOOTER.contact.newsletterPlaceholderKey),
          agreementText: t(FOOTER.contact.agreementTextKey),
          privacyPolicy: link(FOOTER.contact.privacyPolicy),
        },
        copyrightName: t(FOOTER.copyrightNameKey),
        bottomNav: FOOTER.bottomNav.map(link),
        vectorImage: FOOTER.vectorImage,
      } as Omit<FooterProps, 'onSubscribe' | 'tone'>,
      /*
       * The bottom tab bar. `menuLabel` is what appends the fifth tab — `AppShell` owns
       * the off-canvas's open state, so the app cannot build that one itself.
       */
      bottomNav: {
        label: t('shell.bottomNav'),
        menuLabel: t('tab.menu'),
        items: BOTTOM_NAV.map(({ labelKey, ...rest }) => ({ label: t(labelKey), ...rest })),
      },
      sidebar: {
        followTitle: t(SIDEBAR.followTitleKey),
        socials: SIDEBAR.socials,
        sitesTitle: t(SIDEBAR.sitesTitleKey),
        sites: SIDEBAR.sites.map(link),
      },
      notFound: {
        logo: NOT_FOUND.logo,
        name: NOT_FOUND.name,
        code: NOT_FOUND.code,
        title: s(NOT_FOUND.title),
        descr: s(NOT_FOUND.descr),
        verse: {
          text: s(NOT_FOUND.verse.text),
          reference: s(NOT_FOUND.verse.reference),
          translation: NOT_FOUND.verse.translation,
        },
        primaryCta: link(NOT_FOUND.primaryCta),
        secondaryCta: link(NOT_FOUND.secondaryCta),
        linksLabel: t(NOT_FOUND.linksLabelKey),
        links: NOT_FOUND.links.map(link),
      },
    }
  }, [t, s])
}
